import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, FileText, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { companyNav } from "../../components/shared/navConfig";
import { toast } from "sonner";

export default function CompanyMyTasks() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/tasks/mine"); setRows(data); }
      catch { toast.error("Failed to load"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = rows.filter(r => !q || (r.title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardShell nav={companyNav} title="My tasks" subtitle="Everything you have submitted or drafted"
      headerRight={<Link to="/company/post" data-testid="mytasks-new-btn" className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5"><Plus size={13}/> New task</Link>}>
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border flex-1 max-w-md" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
            <Search size={13} className="text-zinc-500"/>
            <input data-testid="mytasks-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search your tasks…" className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 flex-1"/>
          </div>
          <div className="text-xs text-zinc-500">{filtered.length} record{filtered.length !== 1 && "s"}</div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500">
            <FileText size={22} className="mx-auto mb-3 text-zinc-600"/>
            You haven't posted any tasks yet.
            <div className="mt-4">
              <Link to="/company/post" className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5">Post your first task <ArrowRight size={12}/></Link>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="md:hidden space-y-3">
              {filtered.map(t => (
                <div key={t.task_id} data-testid={`mytasks-card-${t.task_id}`}
                     className="p-3 rounded-md border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm truncate" style={{ color: "var(--text)" }}>{t.title}</div>
                      <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>{t.task_id}</div>
                    </div>
                    <StatusBadge status={t.status === "Draft" ? "Pending" : t.status}/>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                    <div><div className="text-[9px] uppercase" style={{ color: "var(--text-dim)" }}>Loc</div>{t.city || t.work_mode || "—"}</div>
                    <div><div className="text-[9px] uppercase" style={{ color: "var(--text-dim)" }}>Budget</div><span className="font-mono" style={{ color: "var(--text)" }}>₹{Number(t.budget || 0).toLocaleString()}</span></div>
                    <div><div className="text-[9px] uppercase" style={{ color: "var(--text-dim)" }}>Deadline</div>{t.deadline || "—"}</div>
                  </div>
                  {t.reviewer_note && (
                    <div className="mt-2 pt-2 border-t text-[11px]" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                      Reviewer: <span style={{ color: "var(--text)" }}>{t.reviewer_note}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <table className="hidden md:table w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 text-left border-b" style={{ borderColor: "var(--border)" }}>
                  <th className="pb-3 font-normal">Task</th>
                  <th className="pb-3 font-normal">Location</th>
                  <th className="pb-3 font-normal">Budget</th>
                  <th className="pb-3 font-normal">Deadline</th>
                  <th className="pb-3 font-normal">Reviewer note</th>
                  <th className="pb-3 font-normal text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.task_id} data-testid={`mytasks-row-${t.task_id}`} className="border-b last:border-b-0" style={{ borderColor: "var(--border)" }}>
                    <td className="py-4">
                      <div className="text-white">{t.title}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{t.task_id}</div>
                    </td>
                    <td className="text-zinc-400 text-xs">{t.city || t.work_mode}</td>
                    <td className="text-white font-mono text-xs">₹{Number(t.budget || 0).toLocaleString()}</td>
                    <td className="text-zinc-400 text-xs">{t.deadline || "—"}</td>
                    <td className="text-zinc-400 text-xs max-w-xs truncate">{t.reviewer_note || "—"}</td>
                    <td className="text-right"><StatusBadge status={t.status === "Draft" ? "Pending" : t.status}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
